import { useState, useEffect, useCallback, useRef } from 'react';
import { convertGraphQLFeedItemToTweet } from './useTweets.converters';
import { fetchTweetsFromGraphQL, fetchHomeFeed } from './useTweets.api';
/**
 * 중앙화된 트윗 관리 훅
 * microblog-lms의 useTweets를 기반으로 edusense 타입에 맞게 최적화
 * 
 * 복잡한 페이지네이션과 상태 관리로 인해 React Query 대신 기존 패턴 유지
 * @param userIdOrOptions 사용자 ID 또는 옵션 객체
 * @returns 트윗 관리를 위한 상태와 함수들
 */ export function useTweets(userIdOrOptions) {
    // 매개변수 파싱 (userId는 number 또는 string 모두 허용)
    const isOptionsObject = typeof userIdOrOptions === 'object' && userIdOrOptions !== null && !Array.isArray(userIdOrOptions);
    const userId = !isOptionsObject && (typeof userIdOrOptions === 'number' || typeof userIdOrOptions === 'string') ? userIdOrOptions : undefined;
    const options = isOptionsObject ? userIdOrOptions : {};
    const { initialTweets = [], pageSize = 20, autoLoad = true } = options;
    // 상태 관리
    const [tweets, setTweets] = useState(initialTweets);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [activeTab, setActiveTab] = useState('tweets');
    const [endCursor, setEndCursor] = useState(undefined);
    const loadingRef = useRef(false);
    // API 서비스는 각 함수에서 직접 사용
    /**
   * 게시물 조회 함수
   */ const fetchTweets = useCallback(async (tabType, targetPage, isRefresh = false)=>{
        console.log(`[useTweets] fetchTweets 호출됨 - tab: ${tabType}, targetPage: ${targetPage}, userId: ${userId}`);
        // 이미 로딩 중이면 중복 호출 방지
        if (loadingRef.current) {
            console.warn('[useTweets] 이미 로딩 중, fetch 중단');
            return;
        }
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        try {
            // 비사용자 특정 호출 (일반 타임라인 - HOME feed)
            if (!userId || userId === '') {
                // GraphQL로 HOME 피드 가져오기 (forYouFeed)
                const cursor = isRefresh || targetPage === 0 ? undefined : endCursor;
                const response = await fetchHomeFeed(pageSize, cursor);
                const feedTweets = response?.tweets || [];
                // GraphQL 응답 데이터를 Tweet 타입으로 변환
                const newTweets = feedTweets.map((feedItem)=>convertGraphQLFeedItemToTweet(feedItem)).filter((tweet)=>tweet !== null);
                console.log('[useTweets] HOME Feed (GraphQL)로 가져온 트윗 수:', newTweets.length);
                if (isRefresh || targetPage === 0) {
                    setTweets(newTweets);
                    setPage(0);
                    setEndCursor(response.endCursor);
                } else {
                    setTweets((prev)=>{
                        const existingIds = new Set(prev.map((t)=>t.id));
                        const uniqueNewTweets = newTweets.filter((t)=>!existingIds.has(t.id));
                        return [
                            ...prev,
                            ...uniqueNewTweets
                        ];
                    });
                    setPage(targetPage);
                    setEndCursor(response.endCursor);
                }
                // hasMore 상태 업데이트 (GraphQL cursor pagination)
                const hasMoreData = response?.hasMore || false;
                setHasMore(hasMoreData);
                console.log('[useTweets] hasMore 상태:', hasMoreData);
                return;
            }
            // GraphQL API 호출 (커서 기반 페이지네이션)
            const cursor = isRefresh || targetPage === 0 ? undefined : endCursor;
            const response = await fetchTweetsFromGraphQL(String(userId), pageSize, cursor);
            // GraphQL 응답 데이터 변환 (컨버터 함수 사용)
            const tweetsData = response.userTweets?.edges || [];
            const newTweets = tweetsData.map((edge)=>convertGraphQLFeedItemToTweet(edge.node)).filter((tweet)=>tweet !== null);
            console.log('[useTweets] GraphQL로 가져온 트윗 IDs:', newTweets.map((t)=>t.id));
            // 페이지네이션 처리
            if (isRefresh || targetPage === 0) {
                console.log('[useTweets] 페이지 0 로드 (새로고침): 기존 트윗 교체');
                setTweets(newTweets);
                setPage(0);
                setEndCursor(response.userTweets?.pageInfo.endCursor || undefined);
            } else {
                setTweets((prev)=>{
                    const existingIds = new Set(prev.map((t)=>t.id));
                    const uniqueNewTweets = newTweets.filter((t)=>!existingIds.has(t.id));
                    console.log('[useTweets] 중복 제외 후 추가될 트윗 IDs:', uniqueNewTweets.map((t)=>t.id));
                    return [
                        ...prev,
                        ...uniqueNewTweets
                    ];
                });
                setPage(targetPage);
                setEndCursor(response.userTweets?.pageInfo.endCursor || undefined);
            }
            // hasMore 상태 업데이트 (GraphQL cursor pagination)
            const hasMoreData = response.userTweets?.pageInfo.hasNextPage || false;
            setHasMore(hasMoreData);
            console.log('[useTweets] hasMore 상태 업데이트:', hasMoreData);
        } catch (err) {
            console.error('게시물 조회 중 오류 발생:', err);
            setError(err instanceof Error ? err.message : '게시물을 불러오는 중 오류가 발생했습니다.');
        } finally{
            loadingRef.current = false;
            setLoading(false);
            console.log('[useTweets] fetchTweets 완료, loading false');
        }
    }, [
        userId,
        pageSize,
        endCursor
    ]);
    // 탭 변경시 게시물 조회 (user ID가 있을 때만)
    useEffect(()=>{
        if (userId) {
            fetchTweets(activeTab, 0, true);
        }
    }, [
        activeTab,
        userId,
        fetchTweets
    ]);
    // 비사용자 특정 사용을 위한 자동 로드
    useEffect(()=>{
        if (autoLoad && !userId && tweets.length === 0) {
            fetchTweets('tweets', 0, true);
        }
    }, [
        autoLoad,
        userId,
        tweets.length,
        fetchTweets
    ]);
    // API 메서드들
    const loadMore = useCallback(async ()=>{
        if (loading || !hasMore) return;
        const nextPage = page + 1;
        await fetchTweets(activeTab, nextPage);
    }, [
        loading,
        hasMore,
        page,
        fetchTweets,
        activeTab
    ]);
    const refresh = useCallback(async ()=>{
        setHasMore(true);
        await fetchTweets(activeTab, 0, true);
    }, [
        fetchTweets,
        activeTab
    ]);
    const addTweet = useCallback((tweet)=>{
        setTweets((prev)=>[
                tweet,
                ...prev
            ]);
    }, []);
    const updateTweet = useCallback((tweetId, updates)=>{
        setTweets((prev)=>prev.map((tweet)=>tweet.id === Number(tweetId) ? {
                    ...tweet,
                    ...updates
                } : tweet));
    }, []);
    const removeTweet = useCallback((tweetId)=>{
        setTweets((prev)=>prev.filter((tweet)=>tweet.id !== Number(tweetId)));
    }, []);
    // 프로필 전용 메서드
    const handleLoadMore = ()=>{
        if (!loading && hasMore) {
            const nextPage = page + 1;
            fetchTweets(activeTab, nextPage);
        }
    };
    const refreshTweets = useCallback(()=>{
        console.log(`[useTweets] refreshTweets 호출됨 - 현재 탭: ${activeTab}`);
        fetchTweets(activeTab, 0, true);
    }, [
        fetchTweets,
        activeTab
    ]);
    return {
        tweets,
        loading,
        error,
        hasMore,
        loadMore,
        refresh,
        addTweet,
        updateTweet,
        removeTweet,
        // Profile-specific
        tweetsLoading: loading,
        tweetsError: error,
        activeTab,
        setActiveTab,
        handleLoadMore,
        refreshTweets
    };
}
